package com.daytimegaming.ourdailyfinances.presentation.home

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.daytimegaming.ourdailyfinances.data.settings.SettingsManager
import com.daytimegaming.ourdailyfinances.domain.Response
import com.daytimegaming.ourdailyfinances.domain.model.Budget
import com.daytimegaming.ourdailyfinances.domain.model.DashboardAccount
import com.daytimegaming.ourdailyfinances.domain.model.DashboardDetail
import com.daytimegaming.ourdailyfinances.domain.model.Holding
import com.daytimegaming.ourdailyfinances.domain.model.Investments
import com.daytimegaming.ourdailyfinances.domain.model.RealEstateHolding
import com.daytimegaming.ourdailyfinances.domain.model.RecurringTransactionStream
import com.daytimegaming.ourdailyfinances.domain.model.SecurityPerformance
import com.daytimegaming.ourdailyfinances.domain.model.Transaction
import com.daytimegaming.ourdailyfinances.domain.dashboard.DashboardEventBus
import com.daytimegaming.ourdailyfinances.domain.plaid.PlaidEventBus
import com.daytimegaming.ourdailyfinances.domain.plaid.PlaidItemsEventBus
import com.daytimegaming.ourdailyfinances.domain.realestate.RealEstateEventBus
import com.daytimegaming.ourdailyfinances.domain.repository.AuthRepository
import com.daytimegaming.ourdailyfinances.domain.repository.TransactionRepository
import com.daytimegaming.ourdailyfinances.domain.usecase.AccountUseCase
import com.daytimegaming.ourdailyfinances.domain.usecase.BudgetUseCase
import com.daytimegaming.ourdailyfinances.domain.usecase.DashboardUseCase
import com.daytimegaming.ourdailyfinances.presentation.components.BalanceTimeframe
import com.daytimegaming.ourdailyfinances.presentation.util.findLatestIsoTimestamp
import kotlinx.coroutines.CompletableDeferred
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.coroutines.withTimeoutOrNull
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.number
import kotlinx.datetime.todayIn
import kotlin.time.Clock

data class BudgetProgress(
    val currentMonthSpending: Double,
    val previousMonthSpending: Double,
    val changePercentage: Double?,
    val progress: Float
)

data class MonthlyCategoryBreakdown(
    val category: String,
    val amount: Double
)

data class MonthlyIncomeVsExpenses(
    val monthName: String,
    val yearMonth: String,
    val income: Double,
    val expenses: Double,
    val netIncome: Double,
    val savingsRate: Double = if (income > 0) ((netIncome / income) * 100.0).coerceIn(-1000.0, 100.0) else 0.0,
    val incomeTransactionCount: Int = 0,
    val expenseTransactionCount: Int = 0,
    val topExpenseCategories: List<MonthlyCategoryBreakdown> = emptyList()
)

sealed class DashboardsScreenState {
    data object Loading : DashboardsScreenState()
    data class Error(val message: String) : DashboardsScreenState()
    data class Loaded(
        val dashboards: List<DashboardDetail>,
        val totalBalance: Double,
        val hasLinkedAccounts: Boolean,
        val transactions: List<Transaction> = emptyList(),
        val recurringTransactions: List<RecurringTransactionStream> = emptyList(),
        val selectedRange: SpendingRange = SpendingRange.OneMonth,
        val budgets: List<Budget> = emptyList(),
        val budgetProgress: Map<String, BudgetProgress> = emptyMap(),
        val incomeVsExpenses: List<MonthlyIncomeVsExpenses> = emptyList(),
        val lastSync: String? = null,
        val securityPerformance: SecurityPerformance? = null,
        val holdings: List<Holding> = emptyList(),
        val isSecurityPerformanceLoading: Boolean = false,
        val selectedTimeframe: BalanceTimeframe = BalanceTimeframe.SixMonths
    ) : DashboardsScreenState() {
        val combinedAccounts: List<DashboardAccount>
            get() = dashboards.flatMap { it.accounts }.distinctBy { it.accountId }

        val combinedRealEstateHoldings: List<RealEstateHolding>
            get() = dashboards.flatMap { it.realEstateHoldings }.distinctBy { it.realEstateHoldingId }

        val combinedHoldings: List<Holding>
            get() = (dashboards.flatMap { it.investments?.holdings ?: emptyList() } + holdings).distinctBy { "${it.accountId}_${it.securityId}" }
    }
}

private data class SecurityAndInvestmentsData(
    val investments: Response<Investments?>,
    val securityPerformance: SecurityPerformance?,
    val isSecurityPerformanceLoading: Boolean,
    val selectedTimeframe: BalanceTimeframe
)

@OptIn(ExperimentalCoroutinesApi::class)
class DashboardsViewModel(
    private val dashboardUseCase: DashboardUseCase,
    private val accountUseCase: AccountUseCase,
    private val transactionRepository: TransactionRepository,
    private val plaidEventBus: PlaidEventBus,
    private val plaidItemsEventBus: PlaidItemsEventBus,
    private val recentSpendingManager: RecentSpendingManager,
    private val budgetUseCase: BudgetUseCase,
    private val settingsManager: SettingsManager,
    private val authRepository: AuthRepository,
    private val realEstateEventBus: RealEstateEventBus,
    private val dashboardEventBus: DashboardEventBus
) : ViewModel() {

    private val _state = MutableStateFlow<DashboardsScreenState>(DashboardsScreenState.Loading)
    val state = _state.asStateFlow()

    private val _isRefreshing = MutableStateFlow(false)
    val isRefreshing: StateFlow<Boolean> = _isRefreshing.asStateFlow()

    private var refreshDeferred: CompletableDeferred<Unit>? = null

    private var currentUserId: String? = null
    private var loadJob: kotlinx.coroutines.Job? = null

    private val _selectedTimeframe = MutableStateFlow(BalanceTimeframe.SixMonths)
    val selectedTimeframe: StateFlow<BalanceTimeframe> = _selectedTimeframe.asStateFlow()

    fun setSelectedTimeframe(timeframe: BalanceTimeframe) {
        _selectedTimeframe.value = timeframe
    }

    private val _securityPerformance = MutableStateFlow<SecurityPerformance?>(null)
    private val _isSecurityPerformanceLoading = MutableStateFlow(false)
    private var hasLoadedSecurityPerformance = false

    private fun loadSecurityPerformance() {
        if (!hasLoadedSecurityPerformance) {
            hasLoadedSecurityPerformance = true
            _isSecurityPerformanceLoading.value = true
            viewModelScope.launch {
                try {
                    val response = transactionRepository.getSecurityPerformance()
                    if (response is Response.Success) {
                        _securityPerformance.value = response.data
                    }
                } catch (e: Exception) {
                    println("ERROR: getSecurityPerformance failed: $e")
                } finally {
                    _isSecurityPerformanceLoading.value = false
                }
            }
        }
    }

    init {
        load()
        viewModelScope.launch {
            plaidEventBus.events.collect { load() }
        }
        viewModelScope.launch {
            plaidItemsEventBus.events.collect { load() }
        }
        viewModelScope.launch {
            authRepository.currentUser().collect { user ->
                currentUserId = user?.uid
                load()
            }
        }
        viewModelScope.launch {
            realEstateEventBus.events.collect { load() }
        }
        viewModelScope.launch {
            dashboardEventBus.events.collect { load() }
        }
    }

    fun setRange(range: SpendingRange) {
        recentSpendingManager.setRange(range)
    }

    fun refresh() {
        if (_isRefreshing.value) return
        val deferred = CompletableDeferred<Unit>()
        refreshDeferred = deferred
        _isRefreshing.value = true
        viewModelScope.launch {
            try {
                val txJob = launch { transactionRepository.refresh() }
                val recurringJob = launch { transactionRepository.refreshRecurringTransactions() }
                val perfJob = launch {
                    try {
                        val response = transactionRepository.getSecurityPerformance()
                        if (response is Response.Success) {
                            _securityPerformance.value = response.data
                        }
                    } catch (e: Exception) {
                        // ignore
                    }
                }
                load()
                withTimeoutOrNull(10_000) {
                    txJob.join()
                    recurringJob.join()
                    perfJob.join()
                    deferred.await()
                }
            } finally {
                _isRefreshing.value = false
                if (refreshDeferred == deferred) {
                    refreshDeferred = null
                }
            }
        }
    }

    fun load() {
        viewModelScope.launch {
            transactionRepository.refresh()
        }
        viewModelScope.launch {
            transactionRepository.refreshRecurringTransactions()
        }
        loadJob?.cancel()
        loadJob = viewModelScope.launch {
            if (_state.value !is DashboardsScreenState.Loaded) {
                _state.update { DashboardsScreenState.Loading }
            }
            
            val dashboardsDetailsFlow = dashboardUseCase.GetDashboards().flatMapLatest { dashboardsResponse ->
                when (dashboardsResponse) {
                    is Response.Error -> flowOf(Response.Error(dashboardsResponse.message))
                    Response.Loading -> flowOf(Response.Loading)
                    is Response.Success -> {
                        val dashboards = dashboardsResponse.data
                        if (dashboards.isEmpty()) {
                            flowOf(Response.Success(emptyList<DashboardDetail>()))
                        } else {
                            val detailFlows = dashboards.map { dashboard ->
                                dashboardUseCase.GetDashboardDetail(dashboard.dashboardId)
                            }
                            combine(detailFlows) { detailsResponses ->
                                val error = detailsResponses.filterIsInstance<Response.Error>().firstOrNull()
                                if (error != null) {
                                    Response.Error(error.message)
                                } else if (detailsResponses.any { it is Response.Loading }) {
                                    Response.Loading
                                } else {
                                    val details = detailsResponses.filterIsInstance<Response.Success<DashboardDetail>>().map { it.data }
                                    Response.Success(details)
                                }
                            }
                        }
                    }
                }
            }

            val dashboardsAndBudgetsFlow = combine(
                dashboardsDetailsFlow,
                budgetUseCase.GetBudgets()
            ) { dashboards, budgets ->
                dashboards to budgets
            }

            val accountsAndItemsFlow = combine(
                accountUseCase.GetAccounts(),
                accountUseCase.GetPlaidItems()
            ) { accounts, items ->
                accounts to items
            }

            val recurringAndRangeFlow = combine(
                transactionRepository.recurringTransactions,
                recentSpendingManager.selectedRange
            ) { recurring, range ->
                recurring to range
            }

            val investmentsAndSecurityFlow = combine(
                transactionRepository.investments,
                _securityPerformance,
                _isSecurityPerformanceLoading,
                _selectedTimeframe
            ) { investments, securityPerformance, isSecurityPerformanceLoading, selectedTimeframe ->
                SecurityAndInvestmentsData(
                    investments = investments,
                    securityPerformance = securityPerformance,
                    isSecurityPerformanceLoading = isSecurityPerformanceLoading,
                    selectedTimeframe = selectedTimeframe
                )
            }

            combine(
                dashboardsAndBudgetsFlow,
                accountsAndItemsFlow,
                transactionRepository.transactions,
                recurringAndRangeFlow,
                investmentsAndSecurityFlow
            ) { dashboardsAndBudgetsResponse, accountsAndItemsResponse, transactionsResponse, recurringAndRangeResponse, investmentsAndSecurityResponse ->
                val (detailsResponse, budgetsResponse) = dashboardsAndBudgetsResponse
                val (accountsResponse, plaidItemsResponse) = accountsAndItemsResponse
                val (recurringResponse, selectedRange) = recurringAndRangeResponse
                val (investmentsResponse, securityPerformance, isSecurityPerformanceLoading, selectedTimeframe) = investmentsAndSecurityResponse
                val isDone = detailsResponse !is Response.Loading &&
                    budgetsResponse !is Response.Loading &&
                    accountsResponse !is Response.Loading &&
                    plaidItemsResponse !is Response.Loading &&
                    transactionsResponse !is Response.Loading &&
                    recurringResponse !is Response.Loading

                val screenState = when {
                    detailsResponse is Response.Error ->
                        DashboardsScreenState.Error(detailsResponse.message)
                    accountsResponse is Response.Error ->
                        DashboardsScreenState.Error(accountsResponse.message)
                    plaidItemsResponse is Response.Error ->
                        DashboardsScreenState.Error(plaidItemsResponse.message)
                    transactionsResponse is Response.Error ->
                        DashboardsScreenState.Error(transactionsResponse.message)
                    recurringResponse is Response.Error ->
                        DashboardsScreenState.Error(recurringResponse.message)
                    budgetsResponse is Response.Error ->
                        DashboardsScreenState.Error(budgetsResponse.message)
                    detailsResponse is Response.Success && accountsResponse is Response.Success && 
                    plaidItemsResponse is Response.Success &&
                    transactionsResponse is Response.Success && recurringResponse is Response.Success &&
                    budgetsResponse is Response.Success -> {
                        val items = plaidItemsResponse.data
                        val accountsMap = accountsResponse.data.associateBy { it.accountId }

                        val mappedDashboards = detailsResponse.data.map { detail ->
                            detail.copy(
                                accounts = detail.accounts.map { account ->
                                    val updatedAccount = accountsMap[account.accountId]
                                    val institutionName = items.find { it.itemId == account.itemId }?.institutionName
                                        ?: updatedAccount?.institutionName
                                        ?: account.institutionName
                                    account.copy(
                                        currentBalance = updatedAccount?.currentBalance ?: account.currentBalance,
                                        availableBalance = updatedAccount?.availableBalance ?: account.availableBalance,
                                        institutionName = institutionName,
                                        loginRequired = updatedAccount?.loginRequired ?: account.loginRequired,
                                        updateRequired = updatedAccount?.updateRequired ?: account.updateRequired
                                    )
                                }
                            )
                        }

                        val total = mappedDashboards
                            .flatMap { it.accounts }
                            .distinctBy { it.accountId }
                            .sumOf {
                                val bal = it.currentBalance ?: 0.0
                                if (it.type.lowercase() == "credit" || it.type.lowercase() == "loan") -bal else bal
                            } + mappedDashboards
                            .flatMap { it.realEstateHoldings }
                            .distinctBy { it.realEstateHoldingId }
                            .sumOf { holding ->
                                holding.netValue
                            }

                        val dashboardTransactions = detailsResponse.data
                            .flatMap { it.transactions }
                            .distinctBy { it.transactionId }
                        val dashboardAccountIds = mappedDashboards.flatMap { it.accounts.map { acc -> acc.accountId } }.toSet()
                        val detailHoldings = detailsResponse.data.flatMap { it.investments?.holdings ?: emptyList() }
                        val repoHoldings = (investmentsResponse as? Response.Success)?.data?.holdings ?: emptyList()
                        val mergedHoldings = (detailHoldings + repoHoldings).distinctBy { "${it.accountId}_${it.securityId}" }
                        val holdings = mergedHoldings.filter { it.accountId in dashboardAccountIds }

                        val hasInvestment = mappedDashboards.any { it.accounts.any { acc -> acc.type.lowercase() == "investment" } }
                        val isLoadingPerf = if (hasInvestment && !hasLoadedSecurityPerformance) {
                            loadSecurityPerformance()
                            true
                        } else {
                            isSecurityPerformanceLoading
                        }

                        val filteredRecurring = recurringResponse.data.filter { stream ->
                            stream.accountId in dashboardAccountIds &&
                            (stream.personalFinanceCategory?.primary != "LOAN_PAYMENTS" ||
                             stream.personalFinanceCategory.detailed == "LOAN_PAYMENTS_MORTGAGE_PAYMENT") &&
                            stream.personalFinanceCategory?.primary != "TRANSFER_OUT" &&
                            stream.isActive
                        }

                        val userId = currentUserId
                        val orderedDashboards = if (userId != null) {
                            val savedOrder = settingsManager.getDashboardOrder(userId)
                            if (savedOrder.isNotEmpty()) {
                                mappedDashboards.sortedWith { d1, d2 ->
                                    val idx1 = savedOrder.indexOf(d1.dashboardId)
                                    val idx2 = savedOrder.indexOf(d2.dashboardId)
                                    when {
                                        idx1 != -1 && idx2 != -1 -> idx1.compareTo(idx2)
                                        idx1 != -1 -> -1
                                        idx2 != -1 -> 1
                                        else -> 0
                                    }
                                }
                            } else {
                                mappedDashboards
                            }
                        } else {
                            mappedDashboards
                        }

                        val orderedBudgets = if (userId != null) {
                            val savedBudgetOrder = settingsManager.getBudgetOrder(userId)
                            if (savedBudgetOrder.isNotEmpty()) {
                                budgetsResponse.data.sortedWith { b1, b2 ->
                                    val idx1 = savedBudgetOrder.indexOf(b1.budgetId)
                                    val idx2 = savedBudgetOrder.indexOf(b2.budgetId)
                                    when {
                                        idx1 != -1 && idx2 != -1 -> idx1.compareTo(idx2)
                                        idx1 != -1 -> -1
                                        idx2 != -1 -> 1
                                        else -> 0
                                    }
                                }
                            } else {
                                budgetsResponse.data
                            }
                        } else {
                            budgetsResponse.data
                        }

                        // Calculate Budget Progress
                        val today = Clock.System.todayIn(TimeZone.currentSystemDefault())
                        val currentMonth = today.month.number
                        val currentYear = today.year
                        val prevMonth = if (currentMonth == 1) 12 else currentMonth - 1
                        val prevYear = if (currentMonth == 1) currentYear - 1 else currentYear
 
                        val progressMap = orderedBudgets.associate { budget ->
                            val relevantTransactions = if (budget.dashboardIds.isNotEmpty()) {
                                mappedDashboards
                                    .filter { it.dashboardId in budget.dashboardIds }
                                    .flatMap { it.transactions }
                                    .distinctBy { it.transactionId }
                            } else {
                                transactionsResponse.data
                            }

                            val budgetTx = relevantTransactions.filter { tx ->
                                tx.amount > 0.0 && tx.activeCategory?.detailed in budget.categories
                            }

                            val currentMonthSpending = budgetTx.filter { tx ->
                                try {
                                    val txDate = LocalDate.parse(tx.date)
                                    txDate.year == currentYear && txDate.month.number == currentMonth
                                } catch (e: Exception) {
                                    false
                                }
                            }.sumOf { it.amount }

                            val previousMonthSpending = budgetTx.filter { tx ->
                                try {
                                    val txDate = LocalDate.parse(tx.date)
                                    txDate.year == prevYear && txDate.month.number == prevMonth
                                } catch (e: Exception) {
                                    false
                                }
                            }.sumOf { it.amount }

                            val changePercentage = if (previousMonthSpending > 0.0) {
                                ((currentMonthSpending - previousMonthSpending) / previousMonthSpending) * 100.0
                            } else {
                                null
                            }

                            val progress = if (budget.targetValue > 0.0) {
                                (currentMonthSpending / budget.targetValue).toFloat()
                            } else {
                                0.0f
                            }

                            budget.budgetId to BudgetProgress(
                                currentMonthSpending = currentMonthSpending,
                                previousMonthSpending = previousMonthSpending,
                                changePercentage = changePercentage,
                                progress = progress
                            )
                        }

                        val incomeVsExpensesData = calculateIncomeVsExpensesData(
                            transactions = dashboardTransactions,
                            dashboards = orderedDashboards,
                            today = today,
                            monthsCount = 12
                        )

                        val lastSyncTimestamp = findLatestIsoTimestamp(
                            items.flatMap { item ->
                                listOf(
                                    item.balancesLastSyncedAt,
                                    item.transactionsLastSyncedAt,
                                    item.recurringLastSyncedAt,
                                    item.investmentsLastSyncedAt
                                )
                            }
                        )

                        DashboardsScreenState.Loaded(
                            dashboards = orderedDashboards,
                            totalBalance = total,
                            hasLinkedAccounts = accountsResponse.data.isNotEmpty(),
                            transactions = dashboardTransactions,
                            recurringTransactions = filteredRecurring,
                            selectedRange = selectedRange,
                            budgets = orderedBudgets,
                            budgetProgress = progressMap,
                            incomeVsExpenses = incomeVsExpensesData,
                            lastSync = lastSyncTimestamp,
                            securityPerformance = securityPerformance,
                            holdings = holdings,
                            isSecurityPerformanceLoading = isLoadingPerf,
                            selectedTimeframe = selectedTimeframe
                        )
                    }
                    else -> {
                        val current = _state.value
                        current as? DashboardsScreenState.Loaded ?: DashboardsScreenState.Loading
                    }
                }
                screenState to isDone
            }.collect { (newState, isDone) ->
                _state.update { newState }
                if (isDone) {
                    refreshDeferred?.complete(Unit)
                }
            }
        }
    }

    fun updateDashboardOrder(order: List<String>) {
        val userId = currentUserId ?: return
        settingsManager.setDashboardOrder(userId, order)
        
        // Update the current Loaded state directly so the UI updates instantly
        val currentState = _state.value
        if (currentState is DashboardsScreenState.Loaded) {
            val sortedDashboards = currentState.dashboards.sortedWith { d1, d2 ->
                val idx1 = order.indexOf(d1.dashboardId)
                val idx2 = order.indexOf(d2.dashboardId)
                when {
                    idx1 != -1 && idx2 != -1 -> idx1.compareTo(idx2)
                    idx1 != -1 -> -1
                    idx2 != -1 -> 1
                    else -> 0
                }
            }
            _state.update { currentState.copy(dashboards = sortedDashboards) }
        }
    }


    fun updateBudgetOrder(order: List<String>) {
        val userId = currentUserId ?: return
        settingsManager.setBudgetOrder(userId, order)
        
        // Update the current Loaded state directly so the UI updates instantly
        val currentState = _state.value
        if (currentState is DashboardsScreenState.Loaded) {
            val sortedBudgets = currentState.budgets.sortedWith { b1, b2 ->
                val idx1 = order.indexOf(b1.budgetId)
                val idx2 = order.indexOf(b2.budgetId)
                when {
                    idx1 != -1 && idx2 != -1 -> idx1.compareTo(idx2)
                    idx1 != -1 -> -1
                    idx2 != -1 -> 1
                    else -> 0
                }
            }
            _state.update { currentState.copy(budgets = sortedBudgets) }
        }
    }
}

fun calculateIncomeVsExpensesData(
    transactions: List<Transaction>,
    dashboards: List<DashboardDetail>,
    today: LocalDate,
    monthsCount: Int = 12
): List<MonthlyIncomeVsExpenses> {
    val nonInvestmentAccountIds = dashboards
        .flatMap { it.accounts }
        .filter { it.type.lowercase() != "investment" }
        .map { it.accountId }
        .toSet()

    val relevantTransactions = transactions.filter { tx ->
        if (nonInvestmentAccountIds.isNotEmpty()) {
            tx.accountId in nonInvestmentAccountIds
        } else true
    }

    val months = ((monthsCount - 1) downTo 0).map { monthsAgo ->
        var year = today.year
        var month = today.month.number - monthsAgo
        while (month <= 0) {
            month += 12
            year -= 1
        }
        year to month
    }

    val monthNames = listOf(
        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
    )

    return months.map { (year, monthNum) ->
        val monthName = monthNames[monthNum - 1]

        val txsForMonth = relevantTransactions.filter { tx ->
            try {
                val parsed = LocalDate.parse(tx.date)
                parsed.year == year && parsed.month.number == monthNum
            } catch (e: Exception) {
                false
            }
        }

        var totalIncome = 0.0
        var totalExpenses = 0.0
        var incomeCount = 0
        var expenseCount = 0
        val categoryExpenseMap = mutableMapOf<String, Double>()

        txsForMonth.forEach { tx ->
            val primary = tx.activeCategory?.primary ?: tx.category.firstOrNull() ?: ""
            val detailed = tx.activeCategory?.detailed ?: ""
            if (primary == "TRANSFER_IN" || primary == "TRANSFER_OUT" ||
                detailed == "LOAN_PAYMENTS_CREDIT_CARD_PAYMENT" ||
                tx.category.any { cat -> cat.equals("Credit Card", ignoreCase = true) || cat.equals("Credit Card Payment", ignoreCase = true) }
            ) {
                return@forEach
            }
            val amount = tx.amount
            if (amount < 0.0 || primary == "INCOME") {
                totalIncome += kotlin.math.abs(amount)
                incomeCount++
            } else if (amount > 0.0 && primary != "INCOME") {
                totalExpenses += amount
                expenseCount++
                val catKey = tx.getRecentSpendingCategoryBreakdownKey()
                categoryExpenseMap[catKey] = (categoryExpenseMap[catKey] ?: 0.0) + amount
            }
        }

        val netIncome = totalIncome - totalExpenses
        val savingsRate = if (totalIncome > 0.0) ((netIncome / totalIncome) * 100.0).coerceIn(-1000.0, 100.0) else 0.0
        val topExpenseCategories = categoryExpenseMap.entries
            .sortedByDescending { it.value }
            .take(3)
            .map { MonthlyCategoryBreakdown(it.key, it.value) }

        MonthlyIncomeVsExpenses(
            monthName = monthName,
            yearMonth = "$monthName $year",
            income = totalIncome,
            expenses = totalExpenses,
            netIncome = netIncome,
            savingsRate = savingsRate,
            incomeTransactionCount = incomeCount,
            expenseTransactionCount = expenseCount,
            topExpenseCategories = topExpenseCategories
        )
    }
}
